// @mui
import { useTheme } from '@mui/material/styles';
import { TypographyProps } from '@mui/material';
// hooks
import useResponsive from '../hooks/useResponsive';

// ----------------------------------------------------------------------

export default function GetFontValue(variant: TypographyProps['variant']) {
  const theme = useTheme();

  const breakpoints = useWidth();

  const key = theme.breakpoints.up(breakpoints === 'xl' ? 'lg' : breakpoints);

  const hasResponsive =
    variant === 'h1' ||
    variant === 'h2' ||
    variant === 'h3' ||
    variant === 'h4' ||
    variant === 'h5' ||
    variant === 'h6';

  // Type-safe access to typography variant
  const typographyVariant = theme.typography[variant as keyof typeof theme.typography];

  const getFont =
    hasResponsive && 
    typeof typographyVariant === 'object' && 
    typographyVariant !== null &&
    key in typographyVariant
      ? (typographyVariant as any)[key]
      : typographyVariant;

  const fontSize = remToPx(getFont.fontSize);

  const lineHeight = typeof typographyVariant === 'object' && 'lineHeight' in typographyVariant
    ? Number(typographyVariant.lineHeight) * fontSize
    : fontSize * 1.5; // fallback line height

    const fontWeight = typeof typographyVariant === 'object' && 'fontWeight' in typographyVariant
    ? typographyVariant.fontWeight
    : 400;

  const letterSpacing = typeof typographyVariant === 'object' && 'letterSpacing' in typographyVariant
    ? typographyVariant.letterSpacing
    : 'normal';

  return { fontSize, lineHeight, fontWeight, letterSpacing };
}

// ----------------------------------------------------------------------

export function remToPx(value: string) {
  return Math.round(parseFloat(value) * 16);
}

export function pxToRem(value: number) {
  return `${value / 16}rem`;
}

export function responsiveFontSizes({ sm, md, lg }: { sm: number; md: number; lg: number }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  };
}

// ----------------------------------------------------------------------

function useWidth() {
  const theme = useTheme();

  const keys = [...theme.breakpoints.keys].reverse();

  return (
    // @ts-ignore not sure what is this
    keys.reduce((output, key) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const matches = useResponsive('up', key);

      return !output && matches ? key : output;
    }, null) || 'xs'
  );
}
